import {
  textUI,
  yesNoUI,
  yesNoSchema,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';

import {
  currentOrPastDateRangeUI,
  currentOrPastDateRangeSchema,
} from 'platform/forms-system/src/js/web-component-patterns/datePatterns';

import { wrapDateRangeUiWithDl } from '../helpers/reviewHelpers';
import { changeDefaultDateHint } from '../helpers/hintChanger';
import { validateNotOnOrBeforeDOB } from '../helpers/dobValidation';

const validateStartDateNotOnOrBeforeDOB = validateNotOnOrBeforeDOB(
  "Enter a valid start date. The start date of education or training cannot be on or before the Veteran's date of birth.",
);
const validateEndDateNotOnOrBeforeDOB = validateNotOnOrBeforeDOB(
  "Enter a valid end date. The end date of education or training cannot be on or before the Veteran's date of birth.",
);

/** @type {PageSchema} */
export default {
  uiSchema: {
    'ui:options': { forceDivWrapper: true },
    ...titleUI('Your education and training before disability'),
    'ui:description':
      'Tell us about any other education or training you received before you became too disabled to work.',

    otherBeforeEducation: yesNoUI({
      title:
        'Did you have any other education or training before you became too disabled to work?',
      labels: {
        Y: 'Yes',
        N: 'No',
      },
      required: () => true,
      useDlWrap: true,
      errorMessages: {
        required:
          'Select a response to tell us if you had education or training before becoming disabled.',
      },
    }),

    educationBeforeDisability: {
      'ui:options': {
        customTitle: 'Education before disability',
        useDlWrap: true,

        expandUnder: 'otherBeforeEducation',
        expandUnderCondition: true,
      },
      typeOfEducation: textUI({
        title: 'Type of education or training',
        useDlWrap: true,
        required: formData => formData.otherBeforeEducation === true,
        errorMessages: {
          required:
            'Tell us what kind of education or training you had before becoming disabled',
        },
      }),
      datesOfTraining: {
        ...wrapDateRangeUiWithDl(
          currentOrPastDateRangeUI(
            { title: 'Start date of education or training' },
            { title: 'End date of education or training' },
          ),
          { 'ui:description': changeDefaultDateHint },
        ),

        from: {
          ...wrapDateRangeUiWithDl(
            currentOrPastDateRangeUI(
              {
                title: 'Start date of education or training',
                validations: [validateStartDateNotOnOrBeforeDOB],
              },
              { title: 'End date of education or training' },
            ),
          ).from,
          'ui:required': formData => formData.otherBeforeEducation === true,
          'ui:description': changeDefaultDateHint,
          'ui:errorMessages': {
            required:
              'Enter the start date (month, day, and 4-digit year) of your education or training',
          },
        },
        to: {
          ...wrapDateRangeUiWithDl(
            currentOrPastDateRangeUI(
              { title: 'Start date of education or training' },
              {
                title: 'End date of education or training',
                validations: [validateEndDateNotOnOrBeforeDOB],
              },
            ),
          ).to,
          'ui:required': formData => formData.otherBeforeEducation === true,
          'ui:description': changeDefaultDateHint,
          'ui:errorMessages': {
            required:
              'Enter the end date (month, day, and 4-digit year) of your education or training',
          },
        },
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      otherBeforeEducation: yesNoSchema,
      educationBeforeDisability: {
        type: 'object',
        properties: {
          typeOfEducation: {
            type: 'string',
            maxLength: 100,
          },
          // Overriding the individual dates required in the schema
          datesOfTraining: { ...currentOrPastDateRangeSchema, required: [] },
        },
      },
    },
    required: ['otherBeforeEducation'],
  },
};
